import React, { useState, useEffect, useMemo } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebaseConfig';
import Modal from './Modal';
import BlockTitle from './BlockTitle';
import Button from './Button';
import { useToast } from '../context/ToastContext';
import {
  DfgSection,
  DfgField,
  RowCard,
  AddRowButton,
  DfgErrors,
  DfgPreview,
} from './DfgFormParts';
import {
  toIban,
  prefillZaumdeForm,
  buildZaumdeRecord,
  validateZaumde,
  buildDfgFilename,
  serializeRecords,
  downloadDfgZip,
  downloadDfgJson,
} from '../lib/dfgGenerator';

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const GenerateZaumdeModal = ({ isOpen, onClose, investmentId, flatId, flatDetails, investmentDetails }) => {
  const { showSuccess, showError } = useToast();

  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(prefillZaumdeForm(flatDetails, investmentDetails));
    setErrors([]);
    setShowPreview(false);
  }, [isOpen, flatDetails, investmentDetails]);

  const record = useMemo(() => (form ? buildZaumdeRecord(form) : null), [form]);

  const paymentsSum = useMemo(() => {
    if (!form) return 0;
    return round2(form.paymentScheduling.reduce((acc, p) => acc + (Number(p.amount) || 0), 0));
  }, [form]);

  if (!isOpen || !form) return null;

  const advance = Number(flatDetails?.an_amount) || 0;
  const formatPLN = (value) =>
    new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const setRow = (listKey, index, key, value) =>
    setForm((prev) => ({
      ...prev,
      [listKey]: prev[listKey].map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    }));

  const addRow = (listKey, factory) =>
    setForm((prev) => ({ ...prev, [listKey]: [...prev[listKey], factory()] }));

  const removeRow = (listKey, index) =>
    setForm((prev) => ({ ...prev, [listKey]: prev[listKey].filter((_, i) => i !== index) }));

  const persist = async () => {
    await setDoc(
      doc(firestore, `investments/${investmentId}/flats/${flatId}`),
      {
        dfg: {
          mrpAccountNumber: toIban(form.mrpAccountNumber),
          subAccountNumber: toIban(form.subAccountNumber),
          transferOwnershipDate: form.transferOwnershipDate || '',
          paymentScheduling: form.paymentScheduling.map((p) => ({
            amount: round2(p.amount),
            paymentDate: p.paymentDate || '',
          })),
          propertyAddress: form.propertyAddress,
        },
      },
      { merge: true }
    );

    // Czesc adresu wspolna dla calej inwestycji - zeby kolejne lokale mialy prefill.
    const first = form.propertyAddress[0];
    if (first) {
      await setDoc(
        doc(firestore, `investments/${investmentId}`),
        {
          dfg: {
            address: {
              street: first.street || '',
              buildingNumber: first.buildingNumber || '',
              zipCode: first.zipCode || '',
              city: first.city || '',
              district: first.district || '',
              commune: first.commune || '',
              province: first.province || '',
            },
          },
        },
        { merge: true }
      );
    }
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      await persist();
      showSuccess('Dane DFG lokalu zapisane');
    } catch (e) {
      console.error(e);
      showError('Nie udało się zapisać danych DFG');
    } finally {
      setBusy(false);
    }
  };

  const handleGenerate = async (format) => {
    const found = validateZaumde(form);
    setErrors(found);
    if (found.length > 0) return;

    setBusy(true);
    try {
      await persist();
      const today = new Date().toISOString().split('T')[0];
      const filename = buildDfgFilename(
        'ZAUMDE',
        investmentDetails?.name || 'Inwestycja',
        today,
        flatDetails?.code
      );

      if (format === 'json') {
        const result = downloadDfgJson([record], filename);
        showSuccess(`Pobrano ${result.filename} (${(result.bytes / 1024).toFixed(1)} kB)`);
      } else {
        const result = await downloadDfgZip([record], filename);
        showSuccess(`Wygenerowano ${result.filename} (${(result.zipBytes / 1024).toFixed(1)} kB)`);
      }
    } catch (e) {
      console.error(e);
      showError(e.message || 'Nie udało się wygenerować pliku');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} wide>
      <BlockTitle>ZAUMDE - zgłoszenie umowy deweloperskiej</BlockTitle>
      <p className="text-sm text-gray-500 mt-1">
        Lokal {flatDetails?.code || '-'}, {investmentDetails?.name || 'Inwestycja'} - dane wstępnie
        uzupełnione z systemu, wszystko można poprawić przed wygenerowaniem pliku.
      </p>

      <DfgSection title="Rachunki">
        <div className="grid grid-cols-2 max-768:grid-cols-1 gap-3">
          <DfgField
            id="zaumde-mrp"
            label="Główny rachunek MRP (IBAN)"
            value={form.mrpAccountNumber}
            onChange={(e) => setField('mrpAccountNumber', e.target.value)}
            placeholder="PL00000000000000000000000000"
          />
          <DfgField
            id="zaumde-subaccount"
            label="Subkonto nabywcy (IBAN)"
            value={form.subAccountNumber}
            onChange={(e) => setField('subAccountNumber', e.target.value)}
            placeholder="PL00000000000000000000000000"
          />
        </div>
        {!investmentDetails?.dfg?.mrpAccountNumber && (
          <p className="text-xs text-amber-700 mt-1">
            Inwestycja nie ma jeszcze zapisanego głównego rachunku MRP. Uzupełnij go w formularzu
            ZAWMRP, żeby kolejne lokale miały go automatycznie.
          </p>
        )}
      </DfgSection>

      <DfgSection title="Umowa">
        <DfgField
          id="zaumde-transfer-date"
          label="Data przeniesienia własności"
          type="date"
          value={form.transferOwnershipDate}
          onChange={(e) => setField('transferOwnershipDate', e.target.value)}
          className="max-w-xs"
        />
        <p className="text-xs text-gray-500 mt-1">
          Data umowy w systemie: {flatDetails?.contract_date || '-'}. To inna data niż przeniesienie
          własności, więc nie jest podstawiana automatycznie.
        </p>
      </DfgSection>

      <DfgSection
        title="Harmonogram wpłat nabywcy"
        hint={
          advance > 0
            ? `Suma transz: ${formatPLN(paymentsSum)} zł, zaliczka w systemie: ${formatPLN(advance)} zł`
            : 'Brak kwoty zaliczki w systemie'
        }
        action={
          <AddRowButton onClick={() => addRow('paymentScheduling', () => ({ amount: '', paymentDate: '' }))}>
            Dodaj transzę
          </AddRowButton>
        }
      >
        {form.paymentScheduling.map((payment, i) => (
          <RowCard
            key={i}
            removable={form.paymentScheduling.length > 1}
            onRemove={() => removeRow('paymentScheduling', i)}
          >
            <div className="grid grid-cols-2 max-768:grid-cols-1 gap-3 pr-6">
              <DfgField
                id={`payment-amount-${i}`}
                label="Kwota (zł)"
                type="number"
                step="0.01"
                min="0"
                value={payment.amount}
                onChange={(e) => setRow('paymentScheduling', i, 'amount', e.target.value)}
              />
              <DfgField
                id={`payment-date-${i}`}
                label="Termin wpłaty"
                type="date"
                value={payment.paymentDate}
                onChange={(e) => setRow('paymentScheduling', i, 'paymentDate', e.target.value)}
              />
            </div>
          </RowCard>
        ))}
        {advance > 0 && paymentsSum !== round2(advance) && (
          <p className="text-xs text-amber-700">
            Suma transz różni się od kwoty zaliczki o {formatPLN(round2(advance) - paymentsSum)} zł.
          </p>
        )}
      </DfgSection>

      <DfgSection
        title="Adres nieruchomości"
        action={
          <AddRowButton
            onClick={() =>
              addRow('propertyAddress', () => ({
                landRegisterNumber: '',
                street: '',
                buildingNumber: '',
                apartmentNumber: '',
                zipCode: '',
                city: '',
                district: '',
                commune: '',
                province: '',
              }))
            }
          >
            Dodaj adres
          </AddRowButton>
        }
      >
        {form.propertyAddress.map((address, i) => (
          <RowCard
            key={i}
            removable={form.propertyAddress.length > 1}
            onRemove={() => removeRow('propertyAddress', i)}
          >
            <div className="grid grid-cols-3 max-768:grid-cols-1 gap-3 pr-6">
              <DfgField
                id={`address-kw-${i}`}
                label="Numer księgi wieczystej lokalu"
                value={address.landRegisterNumber}
                onChange={(e) => setRow('propertyAddress', i, 'landRegisterNumber', e.target.value)}
                placeholder="PO1P/00012345/6"
              />
              <DfgField
                id={`address-street-${i}`}
                label="Ulica"
                value={address.street}
                onChange={(e) => setRow('propertyAddress', i, 'street', e.target.value)}
              />
              <div className="grid grid-cols-2 gap-3">
                <DfgField
                  id={`address-building-${i}`}
                  label="Nr budynku"
                  value={address.buildingNumber}
                  onChange={(e) => setRow('propertyAddress', i, 'buildingNumber', e.target.value)}
                />
                <DfgField
                  id={`address-apartment-${i}`}
                  label="Nr lokalu"
                  value={address.apartmentNumber}
                  onChange={(e) => setRow('propertyAddress', i, 'apartmentNumber', e.target.value)}
                />
              </div>
              <DfgField
                id={`address-zip-${i}`}
                label="Kod pocztowy"
                value={address.zipCode}
                onChange={(e) => setRow('propertyAddress', i, 'zipCode', e.target.value)}
                placeholder="00-000"
              />
              <DfgField
                id={`address-city-${i}`}
                label="Miasto"
                value={address.city}
                onChange={(e) => setRow('propertyAddress', i, 'city', e.target.value)}
              />
              <DfgField
                id={`address-district-${i}`}
                label="Dzielnica"
                value={address.district}
                onChange={(e) => setRow('propertyAddress', i, 'district', e.target.value)}
              />
              <DfgField
                id={`address-commune-${i}`}
                label="Gmina"
                value={address.commune}
                onChange={(e) => setRow('propertyAddress', i, 'commune', e.target.value)}
              />
              <DfgField
                id={`address-province-${i}`}
                label="Województwo"
                value={address.province}
                onChange={(e) => setRow('propertyAddress', i, 'province', e.target.value)}
                placeholder="Wielkopolskie"
              />
            </div>
          </RowCard>
        ))}
      </DfgSection>

      <DfgErrors errors={errors} />

      <DfgPreview
        open={showPreview}
        onToggle={() => setShowPreview((v) => !v)}
        json={serializeRecords([record])}
      />

      <div className="flex justify-end gap-3 mt-6">
        <Button variant="bordered" onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button variant="bordered" onClick={handleSave} disabled={busy}>
          Zapisz dane
        </Button>
        <Button
          variant="bordered"
          title="Sam plik JSON, bez pakowania - do wglądu i archiwum"
          onClick={() => handleGenerate('json')}
          disabled={busy}
        >
          Pobierz JSON
        </Button>
        <Button
          variant="default"
          title="Plik do wgrania na portal DFG"
          onClick={() => handleGenerate('zip')}
          disabled={busy}
        >
          {busy ? 'Pracuję...' : 'Generuj plik ZIP'}
        </Button>
      </div>
    </Modal>
  );
};

export default GenerateZaumdeModal;
